import type { SummaryMetricItem } from './SummaryMetrics'

/**
 * Returns up to 4 stats to render in the shareable Summary image, preferring
 * the engagement primitives that are common across networks:
 *
 *   1. Reach   — impressions / views / viewers / reach
 *   2. Likes   — reactions / likes / favorites
 *   3. Replies — comments / replies
 *   4. Amplify — shares / reposts / retweets / reblogs
 *
 * Within a slot we prefer the first synonym with a non-zero value before
 * falling back to the first synonym present. Networks like Instagram Login API
 * return both `impressions` (deprecated, always 0) and `views` (real) — picking
 * `impressions` here would surface a meaningless 0 when `views` carries the
 * actual reach number.
 *
 * Slots that don't match any available metric fall through to the next-best
 * available metric (preserving the input order from the backend, which already
 * curates per-network), so a channel without "shares" still produces a full
 * 4-tile card.
 */

const SLOT_PREFERENCES: ReadonlyArray<readonly string[]> = [
  ['impressions', 'views', 'viewers', 'reach'],
  ['reactions', 'likes', 'favorites'],
  ['comments', 'replies', 'quotes'],
  ['shares', 'reposts', 'retweets', 'reblogs'],
]

export function pickShareableStats(
  metrics: readonly SummaryMetricItem[],
): SummaryMetricItem[] {
  const remaining = new Map(metrics.map((m) => [m.type, m]))
  const picked: SummaryMetricItem[] = []

  for (const preferences of SLOT_PREFERENCES) {
    const hit =
      preferences.find((type) => (remaining.get(type)?.value ?? 0) > 0) ??
      preferences.find((type) => remaining.has(type))
    if (hit) {
      const metric = remaining.get(hit)
      if (metric) {
        picked.push(metric)
        remaining.delete(hit)
      }
    }
  }

  // Fill any empty slots with whatever's left, preserving backend order.
  // Zero-value leftovers are skipped — otherwise an `impressions: 0` we just
  // demoted out of slot 1 would reappear as the 4th tile.
  for (const metric of metrics) {
    if (picked.length >= 4) break
    if (remaining.has(metric.type) && metric.value > 0) {
      picked.push(metric)
      remaining.delete(metric.type)
    }
  }

  return picked.slice(0, 4)
}
